import { useEffect, useRef } from 'react';
import { useAnimatedText } from '@/hooks/aiChat/useAnimatedText';
import { MessageContent } from './MessageContent/MessageContent';
import { cn } from '@/lib/utils';

interface AnimatedMessageProps {
    content: string;
    isStreaming: boolean;
    onAnimationComplete?: () => void;
    placeholderMap?: Record<string, string>;
}

export const AnimatedMessage = ({ content, isStreaming, onAnimationComplete, placeholderMap }: AnimatedMessageProps) => {
    const { stableText, fadingText, isAnimating } = useAnimatedText(content, isStreaming);
    const wasAnimatingRef = useRef(isAnimating);
    const hasCalledCompleteRef = useRef(false);

    useEffect(() => {
        // Fire callback when animation transitions from animating to not animating
        // or if it was never animating (historical message)
        if (!isAnimating && !hasCalledCompleteRef.current && onAnimationComplete) {
            hasCalledCompleteRef.current = true;
            onAnimationComplete();
        }
        wasAnimatingRef.current = isAnimating;
    }, [isAnimating, onAnimationComplete]);

    return (
        <div className="whitespace-pre-wrap">
            <MessageContent content={stableText} placeholderMap={placeholderMap} />
            {fadingText && (
                <span
                    key={stableText.length}
                    className="animate-[fadeInText_0.15s_ease-out_forwards]"
                >
                    <MessageContent content={fadingText} placeholderMap={placeholderMap} />
                </span>
            )}
            {isAnimating && (
                <span
                    className={cn(
                        'inline-block w-0.5 h-[1em] bg-current ml-0.5 align-middle',
                        'animate-[blink_0.7s_step-end_infinite]',
                    )}
                    aria-hidden="true"
                />
            )}
        </div>
    );
};
